// Game tables — the shapes every game module and the framework share.
//
// No imports and no I/O: game modules, logic.ts, the browser and the node
// tests all import this file. See docs/games-contract.md for how to add a game.

/** Seat index used for the host (trivia host, party MC). Never a chair. */
export const HOST = -1;

export interface Seat { kind: 'human' | 'bot'; userId?: string; name: string }

/** What the next automatic step is (clients use it to time their nudge). */
export type DueKind = 'bot' | 'idle' | 'wake' | 'deal';

export interface TableState {
  kind: string;
  phase: 'lobby' | 'playing' | 'over';
  /** One slot per possible player (length = the game's maxPlayers). */
  seats: (Seat | null)[];
  /** The host, for games that have one (not a player; never on the leaderboard). */
  host?: Seat | null;
  /** Game-specific public state (null in the lobby before the first start). */
  g: any;
  /** Lobby options chosen before the start (game-defined, see GameDef.configure). */
  options?: any;
  /** Id of the game in progress / just finished — results are keyed on it. */
  gameId?: string;
  /** When the next automatic step is due (bot move, game clock, next deal, idle human). */
  dueAt: number | null;
  dueKind?: DueKind;
  /** When the current human turn started (for turn timers). */
  turnSince: number | null;
  /** A short line for the table ("West bid 3", "North timed out"). */
  note?: string;
}

/** Private state per seat (cards, tiles, the secret word). Seat HOST = the host's. */
export type Privs = Record<number, any>;

export interface Ctx {
  now: number;
  rng: () => number;
  seats: (Seat | null)[];
  host: Seat | null;
  options: any;
}

/** A rule was broken: the message goes back to the player as a 400. */
export class RuleError extends Error {
  constructor(msg: string) { super(msg); this.name = 'RuleError'; }
}

export type Outcome = 'win' | 'loss' | 'draw';
export interface SeatResult { seat: number; outcome: Outcome; points?: number }

/** One step's change: new public state and, optionally, changed private rows. */
export interface GameStep<G = any> { g: G; privs?: Privs; note?: string }

/**
 * A game. Everything is pure: (state, input) → new state. Throw RuleError to
 * reject a move. `G` is the public game state, `M` the move payload.
 */
export interface GameDef<G = any, M = any> {
  kind: string;
  label: string;
  minPlayers: number;
  maxPlayers: number;
  /** Seat names ("North", "White"…); default "Seat 1", "Seat 2"… */
  seatNames?: string[];
  /** 'none' (default) | 'optional' | 'required' — required blocks the start until someone hosts. */
  host?: 'none' | 'optional' | 'required';
  /** Party games: players may sit and leave while a game is playing (no bot takeover). */
  joinMidGame?: boolean;
  /** Idle human auto-move after this long (ms). Default 60 s if the game can
   *  move for them (bot or onIdle), otherwise no timeout. null = never. */
  turnTimeoutMs?: number | null;

  /** Validate a lobby options patch; return the new options. */
  configure?(current: any, patch: unknown): any;
  /** A new game (prev = null) or the next hand/round (prev = last state, see betweenHands). */
  start(prev: G | null, ctx: Ctx): GameStep<G>;
  /** Whose turn it is (for timeouts, bots and the UI); null = nobody / everyone at once. */
  turnSeat?(g: G): number | null;
  /** May `actor` (a seat or HOST) move now? Default: actor === turnSeat(g). */
  canAct?(g: G, actor: number): boolean;
  /** Apply a move. `privs` holds every seat's private state. */
  move(g: G, privs: Privs, actor: number, move: M, ctx: Ctx): GameStep<G>;
  /** Computer players (omit for games without bots). */
  bot?: { move(g: G, priv: any, seat: number, ctx: Ctx): M; names?: string[] };
  /** What happens to an idle human on their turn (default: the bot moves for them). */
  onIdle?(g: G, privs: Privs, seat: number, ctx: Ctx): GameStep<G> | null;
  /** Game clock: when the game next needs onWake (absolute ms), or null. */
  wakeAt?(g: G): number | null;
  onWake?(g: G, privs: Privs, ctx: Ctx): GameStep<G>;
  /** Mid-game join/leave (joinMidGame games; onLeave also runs for non-bot games — e.g. forfeit). */
  onJoin?(g: G, privs: Privs, seat: number, ctx: Ctx): GameStep<G>;
  onLeave?(g: G, privs: Privs, seat: number, ctx: Ctx): GameStep<G>;
  /** Hand finished, more to play: start(prev) runs automatically after betweenHandsMs. */
  betweenHands?(g: G): boolean;
  betweenHandsMs?: number;
  isOver(g: G): boolean;
  /** At game over: each seated player's outcome (+ points) for the leaderboard. */
  results?(g: G, ctx: Ctx): SeatResult[];
  /** Extra pause before the next automatic step (e.g. let a full trick show). */
  pauseAfter?(prev: G, g: G): number;
  /** Table note for a move ("West bid 3"). */
  describe?(move: M, who: string, actor: number): string;
}
